import { Cell, flexRender } from '@tanstack/react-table';
import { memo } from 'react';

import { cn } from '@/services/utils';

import { TableCell } from '../Table';

interface CoreGridCellProps<TData> {
    cell: Cell<TData, unknown>;
    wrapContent: any;
    hasPadding?: boolean;
}

const CoreGridCell = <TData,>({ cell, wrapContent, hasPadding }: CoreGridCellProps<TData>) => {
    // LAX-10037: use the TanStack column pinning state as the source of
    // truth so any column the user has pinned via the burger menu — not
    // only the hard-coded `select` / `actions` — becomes sticky.
    // The meta-based fallbacks stay for the initial mount before the
    // TanStack state has hydrated.
    const pinnedSide = cell.column.getIsPinned();
    const isLeftPinned = pinnedSide === 'left' || !!cell.column.columnDef.meta?.leftPinned;
    const isRightPinned = pinnedSide === 'right' || !!cell.column.columnDef.meta?.pinned;

    let leftOffset: number | undefined;
    let rightOffset: number | undefined;
    if (pinnedSide === 'left') {
        leftOffset = cell.column.getStart('left');
    } else if (pinnedSide === 'right') {
        // `getStart('right')` returns the distance from the left edge of the
        // right-pinned group. We want the CSS `right` offset from the
        // viewport, so compute the sum of widths of the right-pinned
        // columns that follow this one.
        const rightPinnedCols = cell.getContext().table.getRightVisibleLeafColumns();
        const idx = rightPinnedCols.findIndex((c) => c.id === cell.column.id);
        let offset = 0;
        for (let i = idx + 1; i < rightPinnedCols.length; i++) {
            offset += rightPinnedCols[i].getSize();
        }
        rightOffset = offset;
    }

    const nowrap = !!cell.column.columnDef.meta?.nowrap;

    return (
        <TableCell
            key={cell.id}
            pinned={isRightPinned}
            className={cn(
                cell.column.columnDef.meta?.cellClassName,
                wrapContent ? 'tw-whitespace-normal tw-break-words tw-overflow-wrap' : '',
                nowrap ? 'tw-whitespace-nowrap' : '',
            )}
            leftPinned={isLeftPinned}
            hasPadding={hasPadding}
            data-column-id={cell.column.id}
            pinnedPosition={cell.column.columnDef.meta?.pinnedPosition}
            leftOffset={leftOffset}
            rightOffset={rightOffset}
        >
            <div className={wrapContent
                ? 'tw-break-words tw-overflow-wrap-anywhere'
                : nowrap
                    ? 'tw-w-max tw-whitespace-nowrap'
                    : 'tw-overflow-hidden tw-min-w-0'}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
        </TableCell>

    );
};

const GridCell = memo(CoreGridCell, (prevProps, nextProps) => {
    const prevMeta = prevProps.cell.column.columnDef.meta;
    const nextMeta = nextProps.cell.column.columnDef.meta;

    if (!prevMeta?.memoizeCells) {
        return false;
    }
    return (
        prevProps.cell.id === nextProps.cell.id &&
        prevProps.wrapContent === nextProps.wrapContent &&
        prevProps.hasPadding === nextProps.hasPadding &&
        prevMeta?.readonly === nextMeta?.readonly
    );
}) as typeof CoreGridCell;

export { GridCell };
